import {
  BodyText,
  Box,
  Flex,
  Heading,
  Link,
} from '@utilitywarehouse/hearth-react';
import {
  ChevronLeftSmallIcon,
  LoginSmallIcon,
} from '@utilitywarehouse/hearth-react-icons';
import { useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { DialogID, useDialog } from '@components/Dialog';
import { FeatureFlags } from '@constants/featureFlags';
import { paths } from '@constants/paths';
import { TUTORIAL_FEEDBACK_SEEN_KEY } from '@constants/storageKeys';
import { InboundCall } from '@features/InboundCall';
import { Profile } from '@features/Profile';
import { TutorialPopup } from '@features/Remote/Invite/TutorialPopup';
import { useFeatureFlag } from '@hooks/useFeatureFlag';
import { useUserInformation } from '@hooks/useUserInformation';
import { useAppointmentContext } from '@providers/AppointmentProvider/AppointmentContext';
import { useBasketContext } from '@providers/basket/BasketProvider/Context';
import { useHostedAttribution } from '@providers/state/HostedAttributionProvider';
import { useAuthContext } from '@services/Auth';
import { AssistantType } from '@src/types/attribution';

import { Logo } from './Logo';
import './styles.scss';

interface HeaderProps {
  title?: string;
  subTitle?: string;
  onBack?(): void;
  hideBackButton?: boolean;
  backButtonLabel?: string;
  isErrorBoundary: boolean;
  isAddressPage?: boolean;
  invertTitle: boolean;
}

const Header = ({
  title,
  subTitle,
  onBack,
  hideBackButton,
  backButtonLabel,
  isErrorBoundary,
  isAddressPage,
  invertTitle,
}: HeaderProps) => {
  const { basketId } = useBasketContext();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { activeClient } = useAuthContext();
  const profileRef = useRef<HTMLDivElement | null>(null);
  const startAgainDialog = useDialog(DialogID.START_AGAIN);
  const userInformation = useUserInformation();

  const { appointment } = useAppointmentContext();
  const { value: isPartnerSessionsEnabled } = useFeatureFlag(
    FeatureFlags.PARTNER_SESSIONS_ENABLED,
    false
  );

  const { hostedId, attributionToken, clearAttribution } =
    useHostedAttribution();
  const handleStartAgain = useCallback(() => {
    if (!appointment && !basketId) {
      return;
    }
    if (isErrorBoundary) {
      navigate(paths.root);
    } else {
      startAgainDialog.setOpen(true);
    }
  }, [appointment, basketId, isErrorBoundary, navigate, startAgainDialog]);

  const showTutorialPop =
    pathname === paths.partner &&
    userInformation?.type === AssistantType.PARTNER;

  return (
    <header className="layout-header__container">
      <Flex direction="column">
        <Box className="layout-header__content" backgroundColor="brand">
          <Flex className="wrapper" justifyContent="between">
            <Logo isClickable={!isAddressPage} onClick={handleStartAgain} />

            <Flex ref={profileRef}>
              <Profile
                attributionToken={attributionToken}
                hostedId={hostedId}
                clearAttribution={clearAttribution}
              />
              {!activeClient && !appointment && !hostedId && <InboundCall />}
              {!activeClient &&
                !hostedId &&
                isPartnerSessionsEnabled &&
                isAddressPage &&
                !appointment && (
                  <Link
                    data-testid="partner-login-button"
                    href="auth/okta/external/login"
                    inverted
                  >
                    <LoginSmallIcon /> Partner Login
                  </Link>
                )}
              {showTutorialPop && (
                <TutorialPopup
                  anchorRef={profileRef}
                  text="You can give feedback on the new sign-up journey any time here."
                  tutorialKey={TUTORIAL_FEEDBACK_SEEN_KEY}
                  showBackdrop={false}
                />
              )}
            </Flex>
          </Flex>
        </Box>

        <Box
          color={invertTitle ? 'inverted' : undefined}
          backgroundColor={invertTitle ? 'brand' : undefined}
          paddingTop="400"
        >
          <Flex className="wrapper" direction="column" spacing="lg">
            {onBack && !hideBackButton && (
              <Link
                className="layout-header__back"
                asChild
                data-testid="navigate-back-button"
                onClick={onBack}
              >
                <button type="button">
                  <ChevronLeftSmallIcon />
                  {backButtonLabel}
                </button>
              </Link>
            )}
            {title && (
              <Heading
                as="h1"
                size="xl"
                className="layout-header__title"
                inverted={invertTitle}
              >
                {title}
              </Heading>
            )}
            {subTitle && <BodyText>{subTitle}</BodyText>}
          </Flex>
        </Box>
      </Flex>
    </header>
  );
};

export type { HeaderProps };
export { Header };
